import type { ComponentChildren } from 'preact'
import { useEffect, useId, useRef } from 'preact/hooks'
import { X } from 'lucide-preact'

interface DomainOverlayProps {
  title: string
  subtitle?: string
  /** What Escape does: the caller decides, since a dialog on top or an action in flight changes the answer. */
  onEscape: () => void
  onClose: () => void
  footer: ComponentChildren
  children: ComponentChildren
}

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]'

/**
 * The full-screen sending-domain overlay: header, scrolling body and footer, content capped to a readable width.
 * It is modal, so focus lands in the body when it opens, Tab wraps inside it rather than reaching the page behind,
 * and focus goes back to whatever opened it when it closes.
 */
export function DomainOverlay({ title, subtitle, onEscape, onClose, footer, children }: DomainOverlayProps) {
  const titleId = useId()
  const overlayRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  // Read through a ref so a re-render with a new handler does not re-register the listener.
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    bodyRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onEscapeRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      opener?.focus()
    }
  }, [])

  const wrapTab = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return
    const elements = [...overlayRef.current!.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(element =>
      !element.closest('details:not([open])') || element.tagName === 'SUMMARY')
    const first = elements[0]
    const last = elements[elements.length - 1]
    if (!first || !last) {
      event.preventDefault()
      return
    }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === bodyRef.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={wrapTab}
    >
      <header className="flex-shrink-0 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white break-all">{title}</h2>
            {subtitle && <p className="text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div ref={bodyRef} tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-8">{children}</div>
      </div>

      <footer className="flex-shrink-0 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-4">{footer}</div>
      </footer>
    </div>
  )
}
